export const normalizeDigits = (value = '') => String(value ?? '').replace(/\D/g, '');

export const normalizeGroupKey = (value = '') => String(value ?? '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ');

export const shouldNotifyOrderReady = (order = {}) => {
  if (!order) return false;
  if (order.source === 'public_link') return false;
  return Boolean(order.createdByUserId || order.createdByEmail || order.createdByPhone);
};

export const toOrderDate = (value) => {
  if (!value) return null;
  const parsed = value.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const toLocalDateKey = (value = new Date()) => {
  const date = toOrderDate(value);
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const isOrderFinalizedToday = (order = {}, now = new Date()) => {
  const finalizedAt = order.deliveredAt || order.updatedAt || order.createdAt;
  return order.status === 'delivered' && toLocalDateKey(finalizedAt) === toLocalDateKey(now);
};

export const buildOrderStatusNotification = (order = {}, status = '') => {
  if (!order.createdByUserId || order.source === 'public_link' || !['ready', 'delivered'].includes(status)) {
    return null;
  }

  const customer = order.customerName || order.table || 'cliente';
  const establishment = order.establishmentName || 'ponto de venda';
  const isReady = status === 'ready';

  return {
    recipientUserId: order.createdByUserId,
    recipientEmail: order.createdByEmail || '',
    type: 'order_status',
    orderStatus: status,
    orderId: order.id || '',
    establishmentId: order.establishmentId || '',
    establishmentName: establishment,
    title: isReady ? 'Pedido pronto' : 'Pedido finalizado',
    message: isReady
      ? `O pedido de ${customer} está pronto em ${establishment}.`
      : `O pedido de ${customer} foi finalizado em ${establishment}.`
  };
};

export const buildCustomerComandaData = ({
  customerName = '',
  customerPhone = '',
  customerCpf = '',
  table = '',
  notes = ''
} = {}) => {
  const normalizedName = String(customerName ?? '').trim();
  const normalizedPhone = normalizeDigits(customerPhone);
  const normalizedCpf = normalizeDigits(customerCpf);
  const normalizedTable = String(table ?? '').trim();
  const normalizedNotes = String(notes ?? '').trim();

  return {
    customerName: normalizedName,
    customerPhone: normalizedPhone,
    customerCpf: normalizedCpf,
    table: normalizedTable,
    notes: normalizedNotes
  };
};

export const buildOrderGroupKey = (order = {}) => {
  const phone = normalizeDigits(order.customerPhone);
  const table = String(order.table ?? '').trim();
  const customerName = String(order.customerName ?? '').trim();

  if (phone) return `phone:${phone}`;
  if (table) return `table:${normalizeGroupKey(table)}`;
  if (customerName) return `name:${normalizeGroupKey(customerName)}`;
  return `order:${order.id || 'standalone'}`;
};

export const calculatePaymentBreakdown = ({ totalAmount = 0, peopleCount = 1, paymentBreakdown = [] } = {}) => {
  const numericTotal = Number(totalAmount) || 0;
  const normalizedPeople = Math.max(1, Number(peopleCount) || 1);
  const breakdown = Array.isArray(paymentBreakdown) ? paymentBreakdown.map(item => ({
    method: item?.method || 'cash',
    amount: Number(item?.amount) || 0
  })) : [];
  const splitTotal = breakdown.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const perPersonValue = normalizedPeople > 0 ? numericTotal / normalizedPeople : 0;

  return {
    totalAmount: numericTotal,
    peopleCount: normalizedPeople,
    paymentBreakdown: breakdown,
    splitTotal,
    perPersonValue,
    remainingAmount: Number((numericTotal - splitTotal).toFixed(2)),
    isExact: Number((numericTotal - splitTotal).toFixed(2)) === 0
  };
};

export const buildPosPaymentRecord = ({
  paymentBreakdown = [],
  userId = '',
  userEmail = '',
  userName = '',
  isOnline = true,
  recordedAt = new Date()
} = {}) => {
  const normalizedBreakdown = (Array.isArray(paymentBreakdown) ? paymentBreakdown : [])
    .map(item => ({
      method: item?.method || 'cash',
      amount: Number(item?.amount) || 0
    }))
    .filter(item => item.amount > 0);

  return {
    paymentMethod: normalizedBreakdown.length > 1
      ? 'mixed'
      : normalizedBreakdown[0]?.method || 'cash',
    paymentBreakdown: normalizedBreakdown,
    paymentRecordedByUserId: userId,
    paymentRecordedByEmail: userEmail,
    paymentRecordedByName: userName,
    deviceRecordedAt: recordedAt instanceof Date ? recordedAt.toISOString() : String(recordedAt || ''),
    recordedOffline: !isOnline
  };
};
